import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  QuestionAnswer as QueryIcon,
  BugReport as TicketIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { teamApi, analyticsApi, activityApi, Activity } from '@/services/api';
import { formatDistanceToNow } from 'date-fns';

// Minimalist palette colors for charts
const COLORS = ['#6366F1', '#8B5CF6', '#10B981', '#F59E0B', '#3B82F6'];

const activityIcons: Record<string, React.ReactNode> = {
  team_created: <AddIcon sx={{ color: '#6366F1', fontSize: 18 }} />,
  team_approved: <ApproveIcon sx={{ color: '#10B981', fontSize: 18 }} />,
  team_rejected: <RejectIcon sx={{ color: '#EF4444', fontSize: 18 }} />,
  team_updated: <SettingsIcon sx={{ color: '#3B82F6', fontSize: 18 }} />,
  member_added: <PersonIcon sx={{ color: '#6366F1', fontSize: 18 }} />,
  member_removed: <PersonIcon sx={{ color: '#9CA3AF', fontSize: 18 }} />,
  config_updated: <SettingsIcon sx={{ color: '#3B82F6', fontSize: 18 }} />,
  query_answered: <QueryIcon sx={{ color: '#6366F1', fontSize: 18 }} />,
  ticket_created: <TicketIcon sx={{ color: '#F59E0B', fontSize: 18 }} />,
  escalation: <TicketIcon sx={{ color: '#EF4444', fontSize: 18 }} />,
  admin_action: <SettingsIcon sx={{ color: '#8B5CF6', fontSize: 18 }} />,
  cache_hit: <QueryIcon sx={{ color: '#10B981', fontSize: 18 }} />,
  system_event: <InfoIcon sx={{ color: '#9CA3AF', fontSize: 18 }} />,
};

const ACTIVITY_FILTERS = [
  { label: 'All', value: '' },
  { label: 'Members', value: 'member' },
  { label: 'Config', value: 'config' },
  { label: 'Queries', value: 'query' },
  { label: 'Tickets', value: 'ticket' },
  { label: 'Escalations', value: 'escalation' },
] as const;

interface AnalyticsPageProps {
  embeddedTeamId?: string;
}

export default function AnalyticsPage({ embeddedTeamId }: AnalyticsPageProps) {
  const { teamId: paramTeamId } = useParams<{ teamId?: string }>();
  const [selectedTeamId, setSelectedTeamId] = useState<string>(paramTeamId || '');
  const [days, setDays] = useState(30);
  const [activityPage, setActivityPage] = useState(0);
  const [activityRowsPerPage, setActivityRowsPerPage] = useState(10);
  const [activityFilter, setActivityFilter] = useState('');

  const isEmbedded = !!embeddedTeamId;

  const { data: teams } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamApi.list({ is_active: true, limit: 100 }).then((res) => res.data.items),
    enabled: !isEmbedded,
  });

  // Filter to only show approved teams
  const approvedTeams = teams?.filter((t) => t.approval_status === 'approved') || [];

  const teamId = embeddedTeamId || selectedTeamId || paramTeamId;

  const { data: dashboard, isLoading } = useQuery({
    queryKey: ['analytics-dashboard', teamId, days],
    queryFn: () => analyticsApi.getDashboard(teamId!, days).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: commandStats } = useQuery({
    queryKey: ['command-stats', teamId],
    queryFn: () => analyticsApi.getCommandStats(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: activityData } = useQuery({
    queryKey: ['team-activities', teamId, activityPage, activityRowsPerPage, activityFilter],
    queryFn: () =>
      activityApi
        .getTeamActivities(teamId!, {
          limit: activityRowsPerPage,
          offset: activityPage * activityRowsPerPage,
        })
        .then((res) => res.data),
    enabled: !!teamId,
  });

  const filteredActivities = activityFilter
    ? (activityData?.items || []).filter((a) => a.activity_type.includes(activityFilter))
    : activityData?.items || [];

  const issuesByTypeData = dashboard?.issues_by_type
    ? Object.entries(dashboard.issues_by_type).map(([name, value]) => ({ name, value }))
    : [];

  // Use issues_by_status for the "Top Topics" chart since issues_by_topic doesn't exist
  const issuesByTopicData = dashboard?.issues_by_status
    ? Object.entries(dashboard.issues_by_status)
        .sort((a, b) => (b[1] as number) - (a[1] as number))
        .slice(0, 10)
        .map(([name, value]) => ({ name, value }))
    : [];

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mb: 3 }}>
        {!isEmbedded && <Typography variant="h5">Analytics</Typography>}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', ml: isEmbedded ? 'auto' : undefined }}>
          {!isEmbedded && (
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 }, flex: { xs: 1, sm: 'none' } }}>
              <InputLabel>Team</InputLabel>
              <Select
                value={selectedTeamId}
                label="Team"
                onChange={(e) => setSelectedTeamId(e.target.value)}
              >
                {approvedTeams.map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 }, flex: { xs: 1, sm: 'none' } }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={days}
              label="Period"
              onChange={(e) => setDays(e.target.value as number)}
            >
              <MenuItem value={7}>Last 7 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
              <MenuItem value={90}>Last 90 days</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {!teamId ? (
        <Alert severity="info">Select a team to view analytics</Alert>
      ) : isLoading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="body2">
                    Total Issues
                  </Typography>
                  <Typography variant="h4">{dashboard?.total_issues || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="body2">
                    Issues Today
                  </Typography>
                  <Typography variant="h4">{dashboard?.total_issues_today || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="body2">
                    Issues This Week
                  </Typography>
                  <Typography variant="h4">{dashboard?.total_issues_week || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="body2">
                    Avg Response Time
                  </Typography>
                  <Typography variant="h4">
                    {dashboard?.avg_response_time_ms
                      ? `${(dashboard.avg_response_time_ms / 1000).toFixed(1)}s`
                      : 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            {/* Issues by Type */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Issues by Type
                  </Typography>
                  {issuesByTypeData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={issuesByTypeData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) =>
                            `${name} (${(percent * 100).toFixed(0)}%)`
                          }
                          outerRadius={100}
                          fill="#6366F1"
                          dataKey="value"
                        >
                          {issuesByTypeData.map((_, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography color="text.secondary" align="center" sx={{ py: 8 }}>
                      No data available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Top Topics */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Top Topics
                  </Typography>
                  {issuesByTopicData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={issuesByTopicData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="name" type="category" width={100} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#6366F1" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography color="text.secondary" align="center" sx={{ py: 8 }}>
                      No data available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Command Usage */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Command Usage
                  </Typography>
                  {commandStats && commandStats.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={commandStats}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="command" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="successful" fill="#10B981" name="Successful" />
                        <Bar dataKey="failed" fill="#EF4444" name="Failed" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography color="text.secondary" align="center" sx={{ py: 8 }}>
                      No command data available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Top Experts */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Top Experts
                  </Typography>
                  {dashboard?.top_experts && dashboard.top_experts.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {dashboard.top_experts.map((expert, index) => (
                        <Box
                          key={expert.name}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 1,
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                          }}
                        >
                          <Typography>
                            {index + 1}. {expert.name}
                          </Typography>
                          <Typography color="primary" fontWeight="medium">
                            {expert.count} issues
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                      No expert data available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Activity Log */}
            {teamId && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">Activity Log</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {ACTIVITY_FILTERS.map((f) => (
                          <Chip
                            key={f.value}
                            label={f.label}
                            size="small"
                            variant={activityFilter === f.value ? 'filled' : 'outlined'}
                            color={activityFilter === f.value ? 'primary' : 'default'}
                            onClick={() => {
                              setActivityFilter(f.value);
                              setActivityPage(0);
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell width={40} />
                            <TableCell>Event</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Actor</TableCell>
                            <TableCell align="right">Time</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredActivities.length > 0 ? (
                            filteredActivities.map((activity: Activity) => (
                              <TableRow key={activity.id} hover>
                                <TableCell>
                                  <Box
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: 1,
                                      backgroundColor: '#F3F4F6',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    {activityIcons[activity.activity_type] || (
                                      <InfoIcon sx={{ color: '#9CA3AF', fontSize: 18 }} />
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {activity.title}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      maxWidth: 300,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {activity.description || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {activity.actor_name || activity.actor_email || 'System'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {formatDistanceToNow(new Date(activity.created_at), {
                                      addSuffix: true,
                                    })}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                <Typography color="text.secondary">No activity data available</Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      component="div"
                      count={activityData?.total || 0}
                      page={activityPage}
                      onPageChange={(_, newPage) => setActivityPage(newPage)}
                      rowsPerPage={activityRowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setActivityRowsPerPage(parseInt(e.target.value, 10));
                        setActivityPage(0);
                      }}
                      rowsPerPageOptions={[10, 25, 50]}
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </Box>
  );
}
